import { useContext, useMemo, useState } from 'react';
import { Card, Modal, Skeleton, Tag, Tooltip } from 'antd';
import { CalendarOutlined, GiftOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { IDashboardUpcomingRaffle } from '../../../../services/http/mappers/DashboardMapper';
import { ThemeContext } from '../../../../contexts/ThemeContext';
// @ts-ignore
import './styles.css';
import GiveawayStatusTag from '../../../Giveaways/components/GiveawayStatusTag';
import { FaChevronRight } from 'react-icons/fa';

interface RaffleCalendarProps {
    data: IDashboardUpcomingRaffle[];
    isLoading: boolean;
}

function parseDateSafely(dateStr: string): Date {
    if (!dateStr) return new Date();
    if (dateStr.includes('T')) return new Date(dateStr);
    if (dateStr.includes(' ')) return new Date(dateStr.replace(' ', 'T'));
    return new Date(`${dateStr}T12:00:00`);
}

function toLocalDateStr(date: Date): string {
    return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0'),
    ].join('-');
}

const formatNumber = (value: number) => new Intl.NumberFormat('pt-BR').format(value || 0);

const formatPeriod = (start: string, end: string) => {
    if (!start || !end) return '';
    const startDate = parseDateSafely(start);
    const endDate = parseDateSafely(end);
    return `de ${format(startDate, 'dd/MM/yyyy')} a ${format(endDate, 'dd/MM/yyyy')}`;
};

const RaffleCalendarSkeleton = ({ isDark }: { isDark: boolean }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[1, 2, 3, 4].map(key => (
            <div
                key={key}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    padding: '14px 16px',
                    borderRadius: 12,
                    background: isDark ? '#141414' : '#fafafa',
                    border: `1px solid ${isDark ? '#2a2a2a' : '#ececec'}`,
                }}
            >
                <Skeleton.Button active size="large" style={{ width: 70, height: 70, borderRadius: 10, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Skeleton.Input active size="small" style={{ width: '70%', height: 18, borderRadius: 4 }} />
                    <Skeleton.Input active size="small" style={{ width: '45%', height: 14, borderRadius: 4 }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Skeleton.Button active size="small" style={{ width: 90, height: 24, borderRadius: 6 }} />
                        <Skeleton.Button active size="small" style={{ width: 110, height: 24, borderRadius: 6 }} />
                    </div>
                </div>
            </div>
        ))}
    </div>
);

const RaffleCardItem = ({
    raffle,
    isDark,
    onClick,
}: {
    raffle: IDashboardUpcomingRaffle;
    isDark: boolean;
    onClick: () => void;
}) => {
    const dateObj = parseDateSafely(raffle.dateOfRaffle);
    const dayNumber = format(dateObj, 'dd');
    const monthShort = format(dateObj, 'MMM', { locale: ptBR }).replace('.', '').toUpperCase();
    const weekDay = format(dateObj, 'EEEE', { locale: ptBR });
    const timeStr = format(dateObj, 'HH:mm');

    return (
        <div
            onClick={onClick}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: '14px 16px',
                borderRadius: 12,
                background: isDark ? '#141414' : '#ffffff',
                border: `1px solid ${isDark ? '#2a2a2a' : '#f0f0f0'}`,
                boxShadow: isDark ? 'none' : '0 2px 6px rgba(0,0,0,0.03)',
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                marginBottom: 10,
            }}
            onMouseEnter={e => {
                e.currentTarget.style.borderColor = '#91caff';
                e.currentTarget.style.boxShadow = '0 4px 14px rgba(22,119,255,0.12)';
                e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={e => {
                e.currentTarget.style.borderColor = isDark ? '#2a2a2a' : '#f0f0f0';
                e.currentTarget.style.boxShadow = isDark ? 'none' : '0 2px 6px rgba(0,0,0,0.03)';
                e.currentTarget.style.transform = 'none';
            }}
        >
            {/* Date Highlight Badge */}
            <div
                style={{
                    minWidth: 70,
                    width: 70,
                    padding: '10px 4px',
                    borderRadius: 10,
                    background: isDark ? '#111d2c' : '#e6f4ff',
                    border: `1px solid ${isDark ? '#173d6b' : '#91caff'}`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <div
                    style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: isDark ? '#69b1ff' : '#0958d9',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        lineHeight: 1,
                    }}
                >
                    {monthShort}
                </div>
                <div
                    style={{
                        fontSize: 24,
                        fontWeight: 800,
                        color: isDark ? '#ffffff' : '#1677ff',
                        lineHeight: 1.1,
                        margin: '3px 0 2px',
                    }}
                >
                    {dayNumber}
                </div>
                <div
                    style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: isDark ? '#aaa' : '#595959',
                        lineHeight: 1,
                        textTransform: 'capitalize',
                    }}
                >
                    {timeStr !== '00:00' && timeStr !== '12:00' ? timeStr : weekDay.slice(0, 3)}
                </div>
            </div>

            {/* Content Details */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                        marginBottom: 4,
                    }}
                >
                    <Tooltip title={raffle.description}>
                        <span
                            style={{
                                fontSize: 17,
                                fontWeight: 600,
                                color: isDark ? '#e8e8e8' : '#1f1f1f',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '100%',
                            }}
                        >
                            {raffle.description}
                        </span>
                    </Tooltip>

                    {raffle.countyName && (
                        <Tag
                            color="blue"
                            style={{
                                borderRadius: 6,
                                fontSize: 12,
                                fontWeight: 500,
                                margin: 0,
                            }}
                        >
                            {raffle.countyName}
                        </Tag>
                    )}

                    <GiveawayStatusTag isValid status={raffle.status} />
                </div>

                <div
                    style={{
                        fontSize: 13,
                        color: isDark ? '#aaa' : '#777',
                        marginBottom: 6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                    }}
                >
                    <CalendarOutlined style={{ fontSize: 13, color: '#1677ff' }} />
                    <span>Período: {formatPeriod(raffle.startPeriod, raffle.endPeriod)}</span>
                </div>

                <div
                    style={{
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                    }}
                >
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '3px 9px',
                            borderRadius: 6,
                            background: isDark ? '#120338' : '#f9f0ff',
                            border: `1px solid ${isDark ? '#2b0e68' : '#d3adf7'}`,
                            color: isDark ? '#b37feb' : '#722ed1',
                            fontSize: 12,
                            fontWeight: 600,
                        }}
                    >
                        <GiftOutlined style={{ fontSize: 12 }} />
                        {formatNumber(raffle.totalTickets)} Bilhetes
                    </span>

                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '3px 9px',
                            borderRadius: 6,
                            background: isDark ? '#061d00' : '#f6ffed',
                            border: `1px solid ${isDark ? '#135200' : '#b7eb8f'}`,
                            color: isDark ? '#95de64' : '#389e0d',
                            fontSize: 12,
                            fontWeight: 600,
                        }}
                    >
                        <UserOutlined style={{ fontSize: 12 }} />
                        {formatNumber(raffle.totalParticipants)} Participantes
                    </span>
                </div>
            </div>

            {/* Chevron Action */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    background: isDark ? '#1f1f1f' : '#f5f5f5',
                    color: '#1677ff',
                    flexShrink: 0,
                    fontSize: 13,
                }}
            >
                <FaChevronRight />
            </div>
        </div>
    );
};

export default function RaffleCalendar({ data, isLoading }: RaffleCalendarProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'month' | 'list'>('month');

    const rafflesByDate = useMemo(() =>
        data.reduce<Record<string, IDashboardUpcomingRaffle[]>>((acc, raffle) => {
            const key = raffle.dateOfRaffle.split('T')[0];
            (acc[key] ??= []).push(raffle);
            return acc;
        }, {}),
        [data]);

    const sortedRaffles = useMemo(() => {
        if (!data || !Array.isArray(data)) return [];
        return [...data].sort((a, b) => {
            const timeA = parseDateSafely(a.dateOfRaffle).getTime();
            const timeB = parseDateSafely(b.dateOfRaffle).getTime();
            if (timeA !== timeB) return timeB - timeA;
            if (a.countyName && b.countyName) {
                const cDiff = a.countyName.localeCompare(b.countyName);
                if (cDiff !== 0) return cDiff;
            }
            return (a.description || '').localeCompare(b.description || '');
        });
    }, [data]);

    const groupedByMonth = useMemo(() => {
        const groups: {
            key: string;
            title: string;
            raffles: IDashboardUpcomingRaffle[];
        }[] = [];

        const map = new Map<string, IDashboardUpcomingRaffle[]>();

        sortedRaffles.forEach(raffle => {
            const d = parseDateSafely(raffle.dateOfRaffle);
            const groupKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            if (!map.has(groupKey)) {
                map.set(groupKey, []);
            }
            map.get(groupKey)!.push(raffle);
        });

        map.forEach((raffles, groupKey) => {
            const [year, month] = groupKey.split('-');
            const d = new Date(Number(year), Number(month) - 1, 1, 12, 0, 0);
            const monthName = format(d, "MMMM 'de' yyyy", { locale: ptBR });
            groups.push({
                key: groupKey,
                title: monthName.charAt(0).toUpperCase() + monthName.slice(1),
                raffles,
            });
        });

        return groups;
    }, [sortedRaffles]);

    const selectedRaffles = selectedDate ? (rafflesByDate[selectedDate] ?? []) : [];

    const selectedDateLabel = selectedDate
        ? format(parseDateSafely(selectedDate), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
        : '';

    function handleNavigate(raffle: IDashboardUpcomingRaffle) {
        const path = raffle.kind === 'auto'
            ? `/sorteios/auto/visualizar/${raffle.id}`
            : `/sorteios/geral/visualizar/${raffle.id}`;
        navigate(path);
    }

    return (
        <Card
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 16 }}>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 20 }}>Calendário de Sorteios</div>
                        <div style={{ fontWeight: 400, fontSize: 16, color: '#888' }}>
                            {viewMode === 'month' ? 'Clique em um dia para ver os detalhes dos sorteios' : 'Todos os próximos sorteios em ordem cronológica'}
                        </div>
                    </div>
                </div>
            }
            extra={
                <div style={{ display: 'flex', gap: 4 }}>
                    {(['month', 'list'] as const).map((mode) => (
                        <button
                            key={mode}
                            type="button"
                            onClick={() => { setViewMode(mode); setSelectedDate(null); }}
                            style={{
                                padding: '4px 12px',
                                borderRadius: 6,
                                border: '1px solid',
                                borderColor: viewMode === mode ? '#1677ff' : (theme === 'light' ? '#d9d9d9' : '#434343'),
                                background: viewMode === mode ? '#1677ff' : (theme === 'light' ? '#fff' : '#1f1f1f'),
                                color: viewMode === mode ? '#fff' : (theme === 'light' ? '#555' : '#aaa'),
                                fontSize: 14,
                                fontWeight: viewMode === mode ? 600 : 400,
                                cursor: 'pointer',
                            }}
                        >
                            {mode === 'month' ? 'Mês' : 'Lista'}
                        </button>
                    ))}
                </div>
            }
            style={{
                background: isDark ? '#1f1f1f' : '#FFFFFF',
                borderRadius: 16,
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                border: 'none',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
            bodyStyle={{ padding: 20, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
            {isLoading ? (
                <RaffleCalendarSkeleton isDark={isDark} />
            ) : viewMode === 'list' ? (
                <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 490, overflowY: 'auto', paddingRight: 4, flex: 1 }}>
                    {sortedRaffles.length === 0 ? (
                        <div style={{ textAlign: 'center', color: '#888', fontSize: 14, padding: '40px 0' }}>
                            Nenhum próximo sorteio agendado
                        </div>
                    ) : (
                        groupedByMonth.map((group) => (
                            <div key={group.key} style={{ marginBottom: 12 }}>
                                <div
                                    style={{
                                        fontSize: 16,
                                        fontWeight: 700,
                                        color: '#1677ff',
                                        padding: '8px 0 6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        borderBottom: `2px solid ${isDark ? '#111d2c' : '#e6f4ff'}`,
                                        marginBottom: 10,
                                    }}
                                >
                                    <CalendarOutlined style={{ fontSize: 15 }} />
                                    <span>{group.title}</span>
                                    <span
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 500,
                                            color: '#888',
                                            background: isDark ? '#262626' : '#f0f0f0',
                                            padding: '1px 8px',
                                            borderRadius: 10,
                                            marginLeft: 'auto',
                                        }}
                                    >
                                        {group.raffles.length} sorteio{group.raffles.length !== 1 ? 's' : ''}
                                    </span>
                                </div>

                                {group.raffles.map((raffle) => (
                                    <RaffleCardItem
                                        key={raffle.id}
                                        raffle={raffle}
                                        isDark={isDark}
                                        onClick={() => handleNavigate(raffle)}
                                    />
                                ))}
                            </div>
                        ))
                    )}
                </div>
            ) : (
                <div style={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 480 }} className={isDark ? 'raffle-calendar-dark' : ''}>
                    <FullCalendar
                        plugins={[dayGridPlugin]}
                        initialView="dayGridMonth"
                        locale={ptBrLocale}
                        headerToolbar={{ left: 'prev', center: 'title', right: 'next' }}
                        height="100%"
                        expandRows={true}
                        dayCellContent={(arg) => {
                            const dateStr = toLocalDateStr(arg.date);
                            const totalRaffles = rafflesByDate[dateStr]?.length;
                            const isSelected = selectedDate === dateStr;
                            return (
                                <div className="raffle-calendar-day-cell">
                                    <span className="raffle-calendar-day-number">{arg.dayNumberText}</span>
                                    {totalRaffles > 0 && (
                                        <button
                                            type="button"
                                            className={`raffle-calendar-day-btn${isSelected ? ' active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedDate(dateStr);
                                            }}
                                        >
                                            <GiftOutlined style={{ fontSize: 11, marginRight: 4 }} />
                                            {totalRaffles} Sorteio{totalRaffles > 1 ? 's' : ''}
                                        </button>
                                    )}
                                </div>
                            );
                        }}
                    />

                    <Modal
                        open={Boolean(selectedDate)}
                        onCancel={() => setSelectedDate(null)}
                        footer={null}
                        width={680}
                        centered
                        destroyOnClose
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0' }}>
                                <div
                                    style={{
                                        width: 38,
                                        height: 38,
                                        borderRadius: '50%',
                                        background: isDark ? '#111d2c' : '#e6f4ff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#1677ff',
                                        flexShrink: 0,
                                    }}
                                >
                                    <CalendarOutlined style={{ fontSize: 18 }} />
                                </div>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 18, color: isDark ? '#e8e8e8' : '#222' }}>
                                        Sorteios em {selectedDateLabel}
                                    </div>
                                    <div style={{ fontSize: 13, color: '#888', fontWeight: 400 }}>
                                        {selectedRaffles.length} sorteio{selectedRaffles.length !== 1 ? 's' : ''} agendado{selectedRaffles.length !== 1 ? 's' : ''} para esta data
                                    </div>
                                </div>
                            </div>
                        }
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 480, overflowY: 'auto', padding: '12px 2px 4px' }}>
                            {selectedRaffles.map((raffle) => (
                                <RaffleCardItem
                                    key={raffle.id}
                                    raffle={raffle}
                                    isDark={isDark}
                                    onClick={() => {
                                        setSelectedDate(null);
                                        handleNavigate(raffle);
                                    }}
                                />
                            ))}
                        </div>
                    </Modal>
                </div>
            )}
        </Card>
    );
}
